import { useEffect, useRef } from 'react'

/**
 * Cloudflare Turnstile for the signup forms (ported from mitohacker-site).
 * Nothing loads on page view: the site key is fetched and Cloudflare's script
 * injected the first time someone focuses a form field (`prime`), so the
 * challenge has usually finished by the time they hit submit. The site key
 * lives in WP (os_forms option, set with scripts/set-form-key.sh); while it is
 * empty every helper resolves to null and the form submits without a token.
 */

type TurnstileApi = {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string
  reset: (id: string) => void
  remove: (id: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

export const FORMS_API = '/wp-json/originalsavage/v1'

let siteKeyPromise: Promise<string | null> | null = null
function getSiteKey() {
  siteKeyPromise ??= fetch(`${FORMS_API}/form-config`)
    .then((r) => (r.ok ? r.json() : null))
    .then((j: { turnstileSiteKey?: string } | null) => j?.turnstileSiteKey || null)
    .catch(() => null)
  return siteKeyPromise
}

let scriptPromise: Promise<TurnstileApi | null> | null = null
function loadScript() {
  scriptPromise ??= new Promise((resolve) => {
    if (window.turnstile) return resolve(window.turnstile)
    const s = document.createElement('script')
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    s.async = true
    s.onload = () => resolve(window.turnstile ?? null)
    s.onerror = () => resolve(null)
    document.head.appendChild(s)
  })
  return scriptPromise
}

export function useTurnstile() {
  const ref = useRef<HTMLDivElement>(null)
  const widgetId = useRef<string | null>(null)
  const token = useRef<string | null>(null)
  const waiters = useRef<((t: string | null) => void)[]>([])
  const started = useRef<Promise<boolean> | null>(null)

  const settle = (t: string | null) => {
    token.current = t
    waiters.current.splice(0).forEach((w) => w(t))
  }

  /** Start the challenge in the background. Resolves false when Turnstile is off. */
  function prime() {
    started.current ??= (async () => {
      const key = await getSiteKey()
      const ts = key ? await loadScript() : null
      if (!key || !ts || !ref.current) return false
      widgetId.current = ts.render(ref.current, {
        sitekey: key,
        theme: 'auto',
        appearance: 'interaction-only',
        callback: (t: string) => settle(t),
        'expired-callback': () => {
          token.current = null
        },
        'error-callback': () => settle(null),
      })
      return true
    })()
    return started.current
  }

  /** The token to send with this submission, or null when Turnstile is off. */
  async function getToken(): Promise<string | null> {
    if (!(await prime())) return null
    if (token.current) return token.current
    return new Promise((resolve) => {
      waiters.current.push(resolve)
      window.setTimeout(() => resolve(token.current), 15000)
    })
  }

  /** Tokens are single-use, so fetch a fresh one after every submission. */
  function reset() {
    token.current = null
    if (widgetId.current) window.turnstile?.reset(widgetId.current)
  }

  useEffect(
    () => () => {
      if (widgetId.current) window.turnstile?.remove(widgetId.current)
    },
    [],
  )

  return { ref, prime, getToken, reset }
}
